/**
 * Delete Confirmation Dialog
 *
 * Reusable confirmation dialog for delete operations
 */

import { useEffect, useRef } from 'react';
import '@tylertech/forge/dialog';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './DeleteConfirmationDialog.css';

export interface DeleteConfirmationDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Title of the dialog */
  title: string;
  /** Message explaining what will be deleted */
  message: string;
  /** Optional additional warning message */
  warning?: string;
  /** Name of the item being deleted (shown in bold) */
  itemName: string;
  /** Callback when user confirms deletion */
  onConfirm: () => void;
  /** Callback when user cancels */
  onCancel: () => void;
  /** Whether the delete operation is in progress */
  isDeleting?: boolean;
  /** Confirm button label when idle (default "Delete") — override for reuse
   * on other destructive-but-not-delete confirmations, e.g. a model package
   * import overwrite (issue #786). */
  confirmLabel?: string;
  /** Confirm button label while in progress (default "Deleting...") */
  confirmingLabel?: string;
}

/**
 * DeleteConfirmationDialog component
 *
 * Displays a confirmation dialog for destructive delete actions
 */
export function DeleteConfirmationDialog({
  open,
  title,
  message,
  warning,
  itemName,
  onConfirm,
  onCancel,
  isDeleting = false,
  confirmLabel = 'Delete',
  confirmingLabel = 'Deleting...',
}: DeleteConfirmationDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLElement & { open: boolean }>(null);

  const DialogElement = 'forge-dialog' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  // Sync dialog open state
  useEffect(() => {
    if (dialogRef.current) {
      if (open) {
        dialogRef.current.open = true;
      } else {
        dialogRef.current.open = false;
      }
    }
  }, [open]);

  const handleDialogClose = (): void => {
    if (!isDeleting) {
      onCancel();
    }
  };

  const titleId = 'delete-dialog-title';
  const descId = 'delete-dialog-desc';

  return (
    <DialogElement
      ref={dialogRef}
      onForge-dialog-close={handleDialogClose}
      className="delete-confirmation-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className="dialog-content">
        <div className="dialog-header">
          <forge-icon name="warning" className="dialog-icon" aria-hidden="true"></forge-icon>
          <h2 id={titleId} className="forge-typography--headline5">
            {title}
          </h2>
        </div>

        <div id={descId} className="dialog-body">
          <p className="forge-typography--body1">{message}</p>
          <p className="forge-typography--body1 item-name">
            <strong>{itemName}</strong>
          </p>
          {warning && (
            <div className="dialog-warning">
              <forge-icon name="error" aria-hidden="true"></forge-icon>
              <p className="forge-typography--body2">{warning}</p>
            </div>
          )}
        </div>

        <div className="dialog-actions">
          <ForgeButton type="button" variant="text" onClick={onCancel} disabled={isDeleting}>
            Cancel
          </ForgeButton>
          <ForgeButton type="button" variant="raised" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting ? confirmingLabel : confirmLabel}
          </ForgeButton>
        </div>
      </div>
    </DialogElement>
  );
}
